import { Injectable } from '@angular/core';
import { SharedUrlRoutes } from '@formbird/shared';
import { OfflineStatusService } from '../offline-status/offline-status.service';
import { IndexedDBService } from '../indexeddb/indexed-db.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { IndexedDBConstants } from '../../constants/IndexedDBConstants';
import { HttpClient, HttpRequest, HttpResponse } from '@angular/common/http';
import { from } from 'rxjs';
import { cloneDeep } from 'lodash';
import { SharedConstants } from '@formbird/types';
import { DataService } from '../data/data.service';

// Get window.URL object
const URL = window.URL; /*|| window.webkitURL;*/
const logger = console;

interface options{
  isOnline?: boolean,
  isCommandBarUpload?: boolean,
  docFieldName?: string

}
@Injectable({
  providedIn: 'root'
})
export class FileProviderService {

  constructor(
    private loggedInUserService: LoggedInUserService,
    private indexedDBService: IndexedDBService,
    private offlineStatusService: OfflineStatusService,
    private httpClient: HttpClient,
    private dataService: DataService
  ) {
  }

  /**
   * Retrieves the file from offline storage first, if unsuccessful retrieve from server.
   */
  getFileResources(file) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const fileNo = _self.offlineStatusService.offlineStatus.connected && file.serverAssignedFileNo
        ? file.serverAssignedFileNo
        : file.fileNo;

      const resources = {
        url: SharedUrlRoutes.serverRoutes.loadFile + '/' + fileNo + '/' + file.fileName.replace(/ /g, '-'),
        base64strData: null
      };

      if (!_self.loggedInUserService.isUserOfflineMode()) {
        resolve(resources);
        return;
      }

      _self.indexedDBService.getFile(file.fileNo, async function(record) {

        if (record) {
          resources.base64strData = record.uploadConfig.data.uploadedFile;
        }
        if (!resources.base64strData && record.uploadConfig.data.file){
          resources.base64strData = await _self.indexedDBService.toBase64Str(record.uploadConfig.data.file)
        }

        resolve(resources);
      });

    });

  }

  /**
   * Sends the file directly to server.
   */
  onlineFileUpload(uploadConfig) {
    // prepare for actual upload

    const fileData = uploadConfig.data;
    const type = fileData.mimetype;
    const self = this;
    const file = new File([fileData.file], fileData.originalname,{ type: type});
    return new Promise((resolve, reject) => {
      this.uploadFileToServer(fileData.fileNo, file, fileData.documentId,fileData.documentName, {isOnline:true} )
        .toPromise().then(success, error);

      function success(response){
        const uploaded = response.body;

        self.dataService.getDocument(fileData.documentId).then((orignalDoc) => {
          const document  = cloneDeep(orignalDoc);
          uploadConfig.serverAssignedFileNo = uploaded.fileNo;
          uploadConfig.basePath = uploaded.basePath;
          self.updateFileInfo(document, uploadConfig);
          self.dataService.deepDiffUpdate(orignalDoc, document).then((data) => {
            resolve({data: uploaded});
          }, (error) => {
            reject(error);
          });

        }).catch(err => {
          logger.error("Error on online file upload: " + JSON.stringify(err));
          resolve({data: uploaded});
          //reject(err)
        });
      }

      function error(error){
        reject(error);
      }

    });


  }

  public uploadFileToServer(fileNo: string, file: File, documentId: string, documentName: string, options: options,
                            ) {
    const postUrl = SharedUrlRoutes.serverRoutes.uploadFiles;
    if (!options){
      options = {};
    }
    if (this.loggedInUserService.isUserOfflineMode() && !options.isOnline){
        let uploadConfig = { data: {
          fileNo: fileNo,  file: file,  originalname: file.name,  mimetype: file.type
          , documentId: documentId, documentName: documentName}, isCommandBarUpload: options.isCommandBarUpload
          , docFieldName: options.docFieldName
        };
        return from(this.offlineFileUpload(uploadConfig));

    } else {

      const myFormData: FormData = new FormData();
      myFormData.append('fileNo', fileNo);
      myFormData.append('file', file);
      myFormData.append('documentId', documentId);
      myFormData.append('documentName', documentName);

      const config = new HttpRequest('POST', postUrl, myFormData, {
        reportProgress: true
      });
      return this.httpClient.request(config);

    }
  }

  /**
   * Saves to offline database. The OfflinePoller sends it to the server later
   */
  private offlineFileUpload(uploadConfig) { //

    const _self = this;

    return new Promise((resolve, reject) => {

      const record = {
        fileNo: uploadConfig.data.fileNo,
        uploadConfig: uploadConfig,
        status: IndexedDBConstants.OFFLINE_STATUS_PENDING // 'UPLOADED' or 'PENDING'
      };

      try {
          const reader = new FileReader();
          reader.onloadend = function(e) {
              const blob = new Blob([ e.target.result ], { type: "image/jpeg" } );
              uploadConfig.data.file = blob;
              _self.indexedDBService.saveFile(record);
              resolve({
                body: {serverAssignedFileNo: uploadConfig.data.fileNo},
                status: 200,
                data: 'OK'
              });
          };
          reader.readAsArrayBuffer(uploadConfig.data.file);


      } catch (err) {
        reject(err);
      }
    });
  }

  downloadFile(file) {

    if (!file.resources.base64strData) {
      window.open(file.resources.url);
      return;
    }

    const objectURL = URL.createObjectURL(this.dataURLtoFile(file.resources.base64strData, file.fileName));

    const anchor = document.createElement('a');
    anchor.download = file.fileName;
    anchor.href = objectURL;
    anchor.click();

    URL.revokeObjectURL(objectURL);
  }

  // helpers
  private dataURLtoFile(dataurl, filename) {

    const arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  uploadFile(uploadConfig) {
    return this.loggedInUserService.isUserOfflineMode() ? this.offlineFileUpload(uploadConfig) : this.onlineFileUpload(uploadConfig);
  }

  private updateFileInfo(document, uploadConfig){

      if (uploadConfig.isCommandBarUpload) {
        const DOCUMENT_FIELD_NAME = SharedConstants.DOCUMENT_FIELD_NAME_UPLOAD_FILE;
        // save as the template's image on navbar
        document.systemHeader[DOCUMENT_FIELD_NAME] = uploadConfig.data.fileNo;
      } else {

        if (uploadConfig.docFieldName){
          const fileRecord: any = {
            fileNo : uploadConfig.data.fileNo,
            fileName: uploadConfig.data.originalname,
            fileType: uploadConfig.data.mimetype,
            serverAssignedFileNo: uploadConfig.serverAssignedFileNo,
            basePath: uploadConfig.basePath,
          };
          let isExists = false;
          let documentFiles = document[uploadConfig.docFieldName];
          if (documentFiles && documentFiles.length > 0){
              for(let i=0; i<documentFiles.length; i++){
                  const item = documentFiles[i];
                  if (item.fileNo === uploadConfig.data.fileNo){
                    item.basePath = uploadConfig.basePath;
                    item.serverAssignedFileNo = uploadConfig.serverAssignedFileNo;
                    isExists = true;
                    break;
                  }
              }
          }else{
            documentFiles = [];
          }
          if (!isExists){
            document[uploadConfig.docFieldName] =  [...documentFiles, fileRecord];
          }

        }

      }


  }
}
